import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchLandingData } from "@/api/auth";

// ============== SIMPLE ICONS DEFINITIONS ==============
const Icons = {
  ChevronRight: () => <span>›</span>,
  Search: () => <span>🔍</span>,
  Menu: () => <span>☰</span>,
  Close: () => <span>✕</span>,
  Home: () => <span>🏠</span>,
  Users: () => <span>👥</span>,
  Book: () => <span>📚</span>,
  Graduation: () => <span>🎓</span>,
  Star: () => <span>★</span>,
  Quote: () => <span>"</span>,
  Phone: () => <span>📞</span>,
  Email: () => <span>✉️</span>,
  Location: () => <span>📍</span>,
  Check: () => <span>✓</span>,
  Clock: () => <span>⏰</span>,
  Play: () => <span>▶</span>,
  ArrowRight: () => <span>→</span>,
  ArrowLeft: () => <span>←</span>,
  Heart: () => <span>❤</span>,
  Share: () => <span>↗</span>,
  Facebook: () => <span>f</span>,
  Twitter: () => <span>𝕏</span>,
  Instagram: () => <span>📷</span>,
  LinkedIn: () => <span>in</span>,
  Question: () => <span>?</span> // أضفت أيقونة السؤال
};

// ============== CSS STYLES ==============
const styles = `
    /* ============== CSS Variables ============== */
    :root {
      --primary: #6a11cb;
      --primary-light: #8a2be2;
      --primary-dark: #4a00e0;
      --secondary: #00dbde;
      --secondary-light: #00c6ff;
      --accent: #ff6b6b;
      --accent-light: #ff9e7d;
      --accent-dark: #ff4757;
      --dark: #1a1a2e;
      --dark-light: #16213e;
      --light: #f8f9fa;
      --light-gray: #e9ecef;
      --gray: #6c757d;
      --success: #00b894;
      --warning: #fdcb6e;
      --danger: #ff7675;
      --info: #00cec9;
      
      --gradient-primary: linear-gradient(135deg, var(--primary), var(--primary-light), var(--secondary));
      --gradient-secondary: linear-gradient(135deg, var(--secondary), var(--secondary-light), #36d1dc);
      --gradient-accent: linear-gradient(135deg, var(--accent), var(--accent-light), #ff9a9e);
      --gradient-dark: linear-gradient(135deg, var(--dark), var(--dark-light), #0f3460);
      --gradient-gold: linear-gradient(135deg, #ffd700, #ffb347, #ffcc33);
      
      --border-radius-xl: 25px;
      --border-radius-lg: 20px;
      --border-radius-md: 15px;
      --border-radius-sm: 10px;
      
      --box-shadow-3d: 15px 15px 30px rgba(0, 0, 0, 0.15), -5px -5px 20px rgba(255, 255, 255, 0.7);
      --box-shadow-lg: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      --box-shadow-md: 0 20px 40px rgba(0, 0, 0, 0.1);
      --box-shadow-sm: 0 10px 30px rgba(0, 0, 0, 0.08);
      --box-shadow-hover: 0 35px 60px rgba(106, 17, 203, 0.15);
      --box-shadow-neon: 0 0 20px rgba(106, 17, 203, 0.7);
      
      --transition-smooth: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
      --transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      --transition-fast: all 0.3s ease;
      
      --glass-bg: rgba(255, 255, 255, 0.12);
      --glass-border: rgba(255, 255, 255, 0.25);
      --glass-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
      
      --neon-primary: 0 0 20px rgba(106, 17, 203, 0.8);
      --neon-secondary: 0 0 20px rgba(0, 219, 222, 0.8);
      --neon-accent: 0 0 20px rgba(255, 107, 107, 0.8);
    }

    /* ============== Reset & Base ============== */
    .academy-landing {
      font-family: 'Cairo', 'Tajawal', 'Poppins', 'Segoe UI', sans-serif;
      line-height: 1.8;
      color: var(--dark);
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f1f3ff 100%);
      direction: rtl;
      overflow-x: hidden;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html {
      scroll-behavior: smooth;
      font-size: 16px;
    }

    ::-webkit-scrollbar {
      width: 12px;
    }

    ::-webkit-scrollbar-track {
      background: linear-gradient(var(--dark), var(--dark-light));
      border-radius: 10px;
    }

    ::-webkit-scrollbar-thumb {
      background: var(--gradient-primary);
      border-radius: 10px;
      border: 2px solid var(--dark-light);
    }

    ::selection {
      background: var(--primary);
      color: white;
      text-shadow: none;
    }

    .container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 0 30px;
    }

    section {
      padding: 120px 0;
      position: relative;
    }

    /* ============== Animations ============== */
    @keyframes float {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      50% { transform: translateY(-25px) rotate(5deg); }
    }

    @keyframes pulse {
      0%, 100% { 
        opacity: 1;
        transform: scale(1);
      }
      50% { 
        opacity: 0.8;
        transform: scale(1.05);
      }
    }

    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }

    @keyframes slideInUp {
      from {
        transform: translateY(50px) translateX(20px);
        opacity: 0;
      }
      to {
        transform: translateY(0) translateX(0);
        opacity: 1;
      }
    }

    @keyframes fadeIn {
      from { 
        opacity: 0;
        transform: translateX(-20px);
      }
      to { 
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    @keyframes neonGlow {
      0%, 100% { 
        box-shadow: var(--neon-primary);
        filter: brightness(1);
      }
      50% { 
        box-shadow: 0 0 30px rgba(106, 17, 203, 1);
        filter: brightness(1.3);
      }
    }

    /* ============== Loading & Error ============== */
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: var(--gradient-dark);
      position: relative;
      overflow: hidden;
    }

    .loading-container::before {
      content: '';
      position: absolute;
      width: 300%;
      height: 300%;
      background: 
        radial-gradient(circle, transparent 20%, rgba(106, 17, 203, 0.1) 21%, transparent 22%),
        radial-gradient(circle, transparent 20%, rgba(0, 219, 222, 0.1) 21%, transparent 22%),
        radial-gradient(circle, transparent 20%, rgba(255, 107, 107, 0.1) 21%, transparent 22%);
      background-size: 150px 150px;
      animation: rotate 20s linear infinite;
    }

    .spinner {
      width: 100px;
      height: 100px;
      border: 5px solid transparent;
      border-radius: 50%;
      border-top: 5px solid var(--secondary);
      border-right: 5px solid var(--primary);
      border-bottom: 5px solid var(--accent);
      border-left: 5px solid var(--success);
      animation: rotate 1.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite;
      margin-bottom: 30px;
      position: relative;
      z-index: 2;
      box-shadow: var(--box-shadow-neon);
    }

    .spinner::before {
      content: '';
      position: absolute;
      top: -10px;
      right: -10px;
      bottom: -10px;
      left: -10px;
      border-radius: 50%;
      border: 3px solid transparent;
      border-top: 3px solid rgba(255, 255, 255, 0.5);
      animation: rotate 3s linear infinite reverse;
    }

    .loading-container h2 {
      color: white;
      font-size: 2.2rem;
      font-weight: 700;
      position: relative;
      z-index: 2;
      background: linear-gradient(45deg, white, var(--secondary), var(--primary-light));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: pulse 2s ease-in-out infinite;
      text-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }

    /* ============== Header ============== */
    .header {
      position: fixed;
      top: 0;
      right: 0;
      left: 0;
      z-index: 1000;
      padding: 25px 0;
      transition: var(--transition-smooth);
      backdrop-filter: blur(15px);
      background: linear-gradient(to left, rgba(255, 255, 255, 0.95), rgba(248, 249, 250, 0.98));
      border-bottom: 2px solid rgba(106, 17, 203, 0.1);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    }

    .header.scrolled {
      padding: 18px 0;
      background: rgba(26, 26, 46, 0.97);
      box-shadow: var(--box-shadow-md);
      border-bottom: 2px solid rgba(106, 17, 203, 0.3);
    }

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 20px;
      text-decoration: none;
      color: var(--dark);
      transition: var(--transition-smooth);
    }

    .header.scrolled .logo {
      color: white;
    }

    .logo-icon {
      width: 60px;
      height: 60px;
      background: var(--gradient-primary);
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 28px;
      box-shadow: var(--box-shadow-neon);
      animation: neonGlow 4s ease-in-out infinite;
      position: relative;
      overflow: hidden;
    }

    .logo-text {
      font-size: 2rem;
      font-weight: 900;
      background: linear-gradient(135deg, var(--primary), var(--primary-light), #9d4edd);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      letter-spacing: -0.5px;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
    }

    .nav-links {
      display: flex;
      gap: 45px;
      list-style: none;
    }

    .nav-link {
      color: var(--dark);
      text-decoration: none;
      font-weight: 700;
      font-size: 1.15rem;
      position: relative;
      padding: 10px 0;
      transition: var(--transition-smooth);
      background: linear-gradient(45deg, var(--primary), var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-size: 200% 100%;
      background-position: 100%;
    }

    .header.scrolled .nav-link {
      color: white;
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .nav-link::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 0;
      height: 4px;
      background: var(--gradient-primary);
      transition: var(--transition-smooth);
      border-radius: 4px;
    }

    .nav-link:hover {
      background-position: 0%;
      transform: translateY(-3px);
    }

    .nav-link:hover::before {
      width: 100%;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 25px;
    }

    .search-btn {
      background: none;
      border: none;
      color: var(--dark);
      font-size: 1.3rem;
      cursor: pointer;
      transition: var(--transition-smooth);
      padding: 12px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f1f3ff, #e9ecef);
      box-shadow: var(--box-shadow-sm);
    }

    .header.scrolled .search-btn {
      color: white;
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }

    .search-btn:hover {
      transform: scale(1.1) rotate(10deg);
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--box-shadow-neon);
    }

    .mobile-menu-btn {
      display: none;
      background: none;
      border: none;
      color: var(--dark);
      font-size: 1.8rem;
      cursor: pointer;
      padding: 12px;
      z-index: 1001;
      transition: var(--transition-smooth);
      border-radius: var(--border-radius-sm);
      background: linear-gradient(135deg, #f1f3ff, #e9ecef);
      box-shadow: var(--box-shadow-sm);
    }

    /* ============== Mobile Menu ============== */
    .mobile-menu {
      position: fixed;
      top: 0;
      right: -100%;
      width: 350px;
      height: 100vh;
      background: rgba(26, 26, 46, 0.98);
      backdrop-filter: blur(25px);
      z-index: 1000;
      transition: var(--transition-smooth);
      padding: 120px 40px 40px;
      display: flex;
      flex-direction: column;
      gap: 30px;
      box-shadow: -20px 0 60px rgba(0, 0, 0, 0.4);
      border-right: 2px solid var(--primary);
    }

    .mobile-menu.open {
      right: 0;
    }

    .mobile-nav-links {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 25px;
    }

    .mobile-nav-link {
      color: white;
      text-decoration: none;
      font-size: 1.4rem;
      font-weight: 700;
      padding: 18px 25px;
      border-radius: var(--border-radius-lg);
      transition: var(--transition-smooth);
      background: rgba(255, 255, 255, 0.08);
      display: flex;
      align-items: center;
      gap: 20px;
      border: 2px solid transparent;
    }

    .mobile-nav-link:hover {
      background: var(--gradient-primary);
      transform: translateX(-15px);
      border-color: rgba(255, 255, 255, 0.3);
      box-shadow: var(--box-shadow-neon);
    }

    /* ============== Buttons ============== */
    .btn {
      padding: 18px 42px;
      border-radius: var(--border-radius-xl);
      font-size: 1.15rem;
      font-weight: 800;
      cursor: pointer;
      transition: var(--transition-smooth);
      border: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 15px;
      text-decoration: none;
      position: relative;
      overflow: hidden;
      z-index: 1;
      letter-spacing: 0.5px;
    }

    .btn:hover {
      transform: translateY(-8px);
    }

    .btn-primary {
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--box-shadow-3d), var(--neon-primary);
    }

    .btn-primary:hover {
      box-shadow: var(--box-shadow-hover), 0 0 40px rgba(106, 17, 203, 0.6);
    }

    .btn-secondary {
      background: var(--gradient-secondary);
      color: white;
      box-shadow: var(--box-shadow-3d), 0 10px 30px rgba(0, 219, 222, 0.4);
    }

    .btn-outline {
      background: transparent;
      color: var(--primary);
      border: 3px solid var(--primary);
      position: relative;
      overflow: hidden;
      box-shadow: var(--box-shadow-sm);
    }

    .btn-outline:hover {
      color: white;
      border-color: transparent;
    }

    .btn-outline::before {
      content: '';
      position: absolute;
      top: 50%;
      right: 50%;
      width: 0;
      height: 0;
      background: var(--gradient-primary);
      border-radius: 50%;
      transform: translate(50%, -50%);
      transition: width 0.8s, height 0.8s;
      z-index: -1;
    }

    .btn-outline:hover::before {
      width: 400%;
      height: 400%;
    }

    .btn-glass {
      background: var(--glass-bg);
      backdrop-filter: blur(15px);
      border: 2px solid var(--glass-border);
      color: white;
      box-shadow: var(--glass-shadow);
    }

    .btn-social {
      width: 50px;
      height: 50px;
      padding: 0;
      border-radius: 50%;
      font-size: 1.1rem;
      background: rgba(255, 255, 255, 0.15);
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.25);
    }

    /* ============== Hero Section ============== */
    .hero {
      min-height: 100vh;
      display: flex;
      align-items: center;
      background: linear-gradient(135deg, var(--dark) 0%, var(--dark-light) 40%, var(--primary-dark) 100%);
      position: relative;
      overflow: hidden;
      padding-top: 120px;
    }

    .hero-content {
      position: relative;
      z-index: 2;
      max-width: 850px;
    }

    .hero-title {
      font-size: 5rem;
      font-weight: 900;
      margin-bottom: 30px;
      line-height: 1.1;
      background: linear-gradient(135deg, white, var(--secondary), var(--primary-light));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      position: relative;
      display: inline-block;
    }

    .hero-subtitle {
      font-size: 1.5rem;
      margin-bottom: 45px;
      color: rgba(255, 255, 255, 0.95);
      line-height: 1.9;
      max-width: 650px;
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    }

    .hero-buttons {
      display: flex;
      gap: 30px;
      flex-wrap: wrap;
      margin-bottom: 70px;
    }

    .hero-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 35px;
      background: var(--glass-bg);
      backdrop-filter: blur(25px);
      border: 2px solid var(--glass-border);
      padding: 50px;
      border-radius: var(--border-radius-xl);
      box-shadow: var(--glass-shadow), inset 0 0 50px rgba(255, 255, 255, 0.1);
    }

    .stat-item {
      text-align: center;
      position: relative;
      padding: 25px;
      border-radius: var(--border-radius-lg);
      background: rgba(255, 255, 255, 0.08);
      transition: var(--transition-smooth);
      border: 1px solid transparent;
    }

    .stat-item:hover {
      transform: translateY(-15px) scale(1.05);
      background: rgba(255, 255, 255, 0.15);
      box-shadow: var(--neon-primary);
      border-color: var(--primary);
    }

    .stat-icon {
      font-size: 3rem;
      margin-bottom: 20px;
      color: var(--secondary);
      filter: drop-shadow(0 0 15px rgba(0, 219, 222, 0.8));
      transition: var(--transition-smooth);
    }

    .stat-number {
      font-size: 3.5rem;
      font-weight: 900;
      background: linear-gradient(135deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 15px;
      line-height: 1;
      text-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }

    .stat-label {
      color: rgba(255, 255, 255, 0.9);
      font-size: 1.1rem;
      font-weight: 700;
      letter-spacing: 0.5px;
    }

    /* ============== Section Headers ============== */
    .section-header {
      text-align: center;
      margin-bottom: 90px;
      position: relative;
    }

    .section-badge {
      display: inline-block;
      background: var(--gradient-primary);
      color: white;
      padding: 12px 32px;
      border-radius: 50px;
      font-size: 1rem;
      font-weight: 800;
      letter-spacing: 1.5px;
      margin-bottom: 30px;
      box-shadow: var(--box-shadow-neon);
      animation: neonGlow 4s ease-in-out infinite;
      position: relative;
      overflow: hidden;
    }

    .section-title {
      font-size: 4rem;
      font-weight: 900;
      margin-bottom: 30px;
      background: linear-gradient(135deg, var(--dark), var(--primary), #9d4edd);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      position: relative;
      display: inline-block;
      text-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }

    .section-subtitle {
      font-size: 1.4rem;
      color: var(--gray);
      max-width: 750px;
      margin: 35px auto 0;
      line-height: 1.9;
      font-weight: 500;
    }

    /* ============== Features Section ============== */
    .features {
      background: white;
      position: relative;
      overflow: hidden;
    }

    .features-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
      gap: 50px;
    }

    .feature-card {
      background: white;
      padding: 60px 50px;
      border-radius: var(--border-radius-xl);
      position: relative;
      overflow: hidden;
      box-shadow: var(--box-shadow-3d);
      transition: var(--transition-smooth);
      border: 3px solid transparent;
      z-index: 1;
    }

    .feature-card:hover {
      transform: translateY(-25px);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover), var(--box-shadow-3d);
    }

    .feature-icon {
      width: 120px;
      height: 120px;
      background: linear-gradient(135deg, #f1f3ff, #e9ecef);
      border-radius: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 35px;
      font-size: 48px;
      color: var(--primary);
      transition: var(--transition-smooth);
      position: relative;
      box-shadow: var(--box-shadow-sm);
    }

    .feature-card:hover .feature-icon {
      transform: scale(1.15) rotate(15deg);
      background: var(--gradient-primary);
      box-shadow: var(--box-shadow-neon);
    }

    .feature-title {
      font-size: 2rem;
      font-weight: 900;
      margin-bottom: 25px;
      color: var(--dark);
      text-align: center;
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .feature-description {
      color: var(--gray);
      font-size: 1.15rem;
      line-height: 1.9;
      text-align: center;
      font-weight: 500;
    }

    /* ============== Academies Section ============== */
    .academies {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f1f3ff 100%);
      position: relative;
      overflow: hidden;
    }

    .academies-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
      gap: 50px;
    }

    .academy-card {
      background: white;
      border-radius: var(--border-radius-xl);
      overflow: hidden;
      box-shadow: var(--box-shadow-3d);
      transition: var(--transition-smooth);
      cursor: pointer;
      border: 3px solid transparent;
      position: relative;
    }

    .academy-card:hover {
      transform: translateY(-25px) scale(1.03);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover), var(--box-shadow-3d);
    }

    .academy-image {
      height: 300px;
      background: linear-gradient(135deg, var(--primary), var(--primary-light), var(--secondary));
      position: relative;
      overflow: hidden;
      transition: transform 0.8s;
    }

    .academy-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .academy-content {
      padding: 50px 40px;
      background: white;
    }

    .academy-title {
      font-size: 2rem;
      font-weight: 900;
      margin-bottom: 25px;
      color: var(--dark);
      position: relative;
      padding-right: 25px;
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .academy-description {
      color: var(--gray);
      font-size: 1.15rem;
      line-height: 1.9;
      margin-bottom: 30px;
      font-weight: 500;
    }

    .academy-link {
      display: inline-flex;
      align-items: center;
      gap: 20px;
      color: var(--primary);
      font-weight: 800;
      font-size: 1.2rem;
      padding: 15px 35px;
      background: rgba(106, 17, 203, 0.12);
      border-radius: 50px;
      transition: var(--transition-smooth);
      border: 2px solid transparent;
    }

    .academy-card:hover .academy-link {
      background: var(--gradient-primary);
      color: white;
      transform: translateX(-15px);
      box-shadow: var(--neon-primary);
      border-color: rgba(255, 255, 255, 0.3);
    }

    /* ============== Plans Section ============== */
    .plans-section {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f1f3ff 100%);
      position: relative;
      overflow: hidden;
    }

    .plans-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
      gap: 50px;
      max-width: 1300px;
      margin: 0 auto;
    }

    .plan-card {
      background: white;
      border-radius: var(--border-radius-xl);
      padding: 60px 50px;
      box-shadow: var(--box-shadow-3d);
      transition: var(--transition-smooth);
      position: relative;
      border: 3px solid transparent;
      text-align: center;
    }

    .plan-card.featured {
      transform: scale(1.08);
      border-color: var(--primary);
      background: linear-gradient(135deg, white, #f8f9fa);
      box-shadow: var(--box-shadow-hover), var(--box-shadow-3d);
      z-index: 2;
    }

    .featured-badge {
      position: absolute;
      top: -25px;
      right: 50%;
      transform: translateX(50%);
      background: var(--gradient-gold);
      color: var(--dark);
      padding: 15px 45px;
      border-radius: 50px;
      font-size: 1.1rem;
      font-weight: 900;
      box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
      z-index: 3;
      display: flex;
      align-items: center;
      gap: 15px;
      letter-spacing: 1px;
      border: 3px solid white;
    }

    .plan-header {
      margin-bottom: 45px;
      padding-bottom: 35px;
      border-bottom: 3px solid var(--light-gray);
    }

    .plan-title {
      font-size: 2.2rem;
      font-weight: 900;
      margin-bottom: 25px;
      color: var(--dark);
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .plan-price {
      margin-bottom: 15px;
    }

    .price-amount {
      font-size: 4.5rem;
      font-weight: 900;
      color: var(--primary-dark);
      line-height: 1;
      background: linear-gradient(135deg, var(--primary-dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }

    .price-period {
      color: var(--gray);
      font-size: 1.2rem;
      font-weight: 700;
      letter-spacing: 0.5px;
    }

    .plan-features {
      margin-bottom: 45px;
    }

    .features-list {
      list-style: none;
      text-align: right;
    }

    .features-list li {
      display: flex;
      align-items: center;
      gap: 20px;
      margin-bottom: 25px;
      color: var(--dark);
      font-size: 1.15rem;
      padding: 15px 25px;
      border-radius: var(--border-radius-md);
      background: rgba(106, 17, 203, 0.07);
      transition: var(--transition-smooth);
      font-weight: 600;
    }

    .features-list li:hover {
      background: rgba(106, 17, 203, 0.15);
      transform: translateX(-15px);
    }

    .features-list li svg {
      color: var(--success);
      font-size: 1.4rem;
      flex-shrink: 0;
    }

    /* ============== Testimonials ============== */
    .testimonials {
      background: white;
      position: relative;
      overflow: hidden;
    }

    .testimonials-slider {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
      gap: 50px;
      max-width: 1400px;
      margin: 0 auto;
    }

    .testimonial-card {
      background: white;
      border-radius: var(--border-radius-xl);
      padding: 60px 50px;
      box-shadow: var(--box-shadow-3d);
      transition: var(--transition-smooth);
      position: relative;
      overflow: hidden;
      border: 3px solid transparent;
    }

    .testimonial-card:hover {
      transform: translateY(-25px);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover), var(--box-shadow-3d);
    }

    .testimonial-content {
      font-size: 1.3rem;
      line-height: 2;
      color: var(--dark);
      margin-bottom: 45px;
      font-style: italic;
      position: relative;
      z-index: 2;
      font-weight: 500;
    }

    .testimonial-author {
      display: flex;
      align-items: center;
      gap: 25px;
    }

    .author-avatar {
      width: 85px;
      height: 85px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 2rem;
      font-weight: 900;
      flex-shrink: 0;
      box-shadow: var(--neon-primary);
      transition: var(--transition-smooth);
    }

    .author-info {
      text-align: right;
      flex: 1;
    }

    .author-name {
      font-size: 1.5rem;
      font-weight: 900;
      margin-bottom: 8px;
      color: var(--dark);
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .author-role {
      color: var(--primary);
      font-weight: 800;
      font-size: 1.1rem;
      letter-spacing: 0.5px;
    }

    .rating {
      display: flex;
      gap: 8px;
      color: var(--warning);
      margin-top: 15px;
    }

    /* ============== FAQ ============== */
    .faq {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f1f3ff 100%);
      position: relative;
      overflow: hidden;
    }

    .faq-list {
      max-width: 950px;
      margin: 0 auto;
    }

    .faq-item {
      background: white;
      border-radius: var(--border-radius-xl);
      margin-bottom: 25px;
      overflow: hidden;
      border: 3px solid transparent;
      transition: var(--transition-smooth);
      box-shadow: var(--box-shadow-3d);
    }

    .faq-question {
      padding: 35px 50px;
      display: flex;
      align-items: center;
      gap: 25px;
      cursor: pointer;
      transition: var(--transition-smooth);
      background: white;
    }

    .faq-item.active .faq-question {
      background: var(--gradient-primary);
      color: white;
    }

    .question-text {
      font-size: 1.4rem;
      font-weight: 800;
      flex: 1;
      margin: 0;
      transition: var(--transition-smooth);
    }

    .faq-item.active .question-text {
      color: white;
    }

    .arrow-icon {
      transition: var(--transition-smooth);
      color: var(--gray);
      font-size: 1.4rem;
    }

    .faq-item.active .arrow-icon {
      transform: rotate(90deg);
      color: white;
    }

    .faq-answer {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.8s;
    }

    .faq-item.active .faq-answer {
      max-height: 600px;
    }

    .faq-answer-content {
      padding: 0 50px 50px;
      color: var(--gray);
      line-height: 2;
      font-size: 1.15rem;
      font-weight: 500;
    }

    /* ============== CTA Section ============== */
    .cta {
      background: var(--gradient-dark);
      color: white;
      text-align: center;
      padding: 180px 0;
      position: relative;
      overflow: hidden;
    }

    .cta-title {
      font-size: 5rem;
      font-weight: 900;
      margin-bottom: 35px;
      background: linear-gradient(135deg, white, var(--secondary), var(--primary-light));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1.1;
      text-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    }

    .cta-subtitle {
      font-size: 1.6rem;
      opacity: 0.95;
      margin-bottom: 60px;
      max-width: 750px;
      margin-left: auto;
      margin-right: auto;
      line-height: 1.9;
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      font-weight: 500;
    }

    .cta-buttons {
      display: flex;
      gap: 30px;
      justify-content: center;
      flex-wrap: wrap;
    }

    /* ============== Contact Section ============== */
    .contact {
      background: white;
      position: relative;
      overflow: hidden;
    }

    .contact-wrapper {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 70px;
      align-items: center;
    }

    .contact-info {
      padding-left: 40px;
    }

    .contact-item {
      display: flex;
      align-items: center;
      gap: 30px;
      margin-bottom: 40px;
      color: var(--dark);
      padding: 30px 35px;
      background: white;
      border-radius: var(--border-radius-xl);
      box-shadow: var(--box-shadow-3d);
      transition: var(--transition-smooth);
      border: 3px solid transparent;
    }

    .contact-item:hover {
      transform: translateX(-20px);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover), var(--box-shadow-3d);
    }

    .contact-icon {
      width: 85px;
      height: 85px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 2rem;
      flex-shrink: 0;
      box-shadow: var(--neon-primary);
      transition: var(--transition-smooth);
    }

    .contact-item:hover .contact-icon {
      transform: scale(1.15) rotate(20deg);
      box-shadow: 0 0 40px rgba(106, 17, 203, 0.8);
    }

    .contact-item h4 {
      font-size: 1.5rem;
      font-weight: 900;
      margin-bottom: 12px;
      color: var(--dark);
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .contact-item p {
      color: var(--gray);
      font-size: 1.15rem;
      line-height: 1.8;
      font-weight: 500;
    }

    .social-links {
      display: flex;
      gap: 20px;
      margin-top: 50px;
    }

    .contact-form {
      background: white;
      padding: 60px 50px;
      border-radius: var(--border-radius-xl);
      box-shadow: var(--box-shadow-3d);
      position: relative;
      overflow: hidden;
      border: 3px solid transparent;
    }

    .contact-form h3 {
      font-size: 2.5rem;
      font-weight: 900;
      margin-bottom: 45px;
      color: var(--dark);
      text-align: center;
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .form-group {
      margin-bottom: 35px;
    }

    .form-label {
      display: block;
      margin-bottom: 15px;
      font-weight: 800;
      color: var(--dark);
      font-size: 1.15rem;
      padding-right: 5px;
    }

    .form-input {
      width: 100%;
      padding: 20px 30px;
      border: 3px solid var(--light-gray);
      border-radius: var(--border-radius-lg);
      font-size: 1.15rem;
      transition: var(--transition-smooth);
      background: white;
    }

    .form-input:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 6px rgba(106, 17, 203, 0.15);
      transform: translateY(-5px);
    }

    /* ============== Footer ============== */
    .footer {
      background: var(--gradient-dark);
      color: white;
      padding: 120px 0 50px;
      position: relative;
      overflow: hidden;
    }

    .footer-content {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 60px;
      margin-bottom: 80px;
    }

    .footer-column h3 {
      font-size: 1.8rem;
      font-weight: 900;
      margin-bottom: 40px;
      color: white;
      position: relative;
      padding-bottom: 20px;
      background: linear-gradient(135deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .footer-links {
      list-style: none;
    }

    .footer-links li {
      margin-bottom: 20px;
    }

    .footer-links a {
      color: rgba(255, 255, 255, 0.9);
      text-decoration: none;
      transition: var(--transition-smooth);
      display: flex;
      align-items: center;
      gap: 15px;
      font-size: 1.15rem;
      padding: 12px 0;
      border-radius: var(--border-radius-sm);
      font-weight: 600;
    }

    .footer-links a:hover {
      color: var(--secondary);
      padding-right: 20px;
      background: rgba(255, 255, 255, 0.08);
    }

    .newsletter {
      margin-top: 35px;
    }

    .newsletter-input {
      width: 100%;
      padding: 18px 25px;
      background: rgba(255, 255, 255, 0.12);
      border: 2px solid rgba(255, 255, 255, 0.25);
      border-radius: var(--border-radius-lg);
      color: white;
      font-size: 1.1rem;
      margin-bottom: 20px;
    }

    .copyright {
      text-align: center;
      padding-top: 50px;
      border-top: 2px solid rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 0.8);
      font-size: 1.1rem;
      font-weight: 600;
      letter-spacing: 0.5px;
    }

    /* ============== Responsive ============== */
    @media (max-width: 1200px) {
      .hero-title { font-size: 4rem; }
      .section-title { font-size: 3rem; }
      .cta-title { font-size: 4rem; }
      .features-grid { grid-template-columns: repeat(2, 1fr); }
      .academies-grid { grid-template-columns: repeat(2, 1fr); }
      .plans-grid { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 992px) {
      .hero-title { font-size: 3.5rem; }
      .nav-links { display: none; }
      .mobile-menu-btn { display: block; }
      .contact-wrapper { grid-template-columns: 1fr; gap: 60px; }
      .footer-content { grid-template-columns: repeat(2, 1fr); }
      .hero-stats { grid-template-columns: repeat(2, 1fr); }
      .features-grid { grid-template-columns: 1fr; }
      .academies-grid { grid-template-columns: 1fr; }
      .plans-grid { grid-template-columns: 1fr; }
      .testimonials-slider { grid-template-columns: 1fr; }
    }

    @media (max-width: 768px) {
      .hero { padding: 180px 0 100px; }
      .hero-title { font-size: 3rem; }
      .hero-subtitle { font-size: 1.3rem; }
      .hero-buttons { flex-direction: column; width: 100%; }
      .hero-buttons .btn { width: 100%; justify-content: center; }
      .section-title { font-size: 2.5rem; }
      .section-badge { font-size: 0.9rem; padding: 10px 25px; }
      .cta-title { font-size: 3rem; }
      .cta { padding: 120px 0; }
      .contact-form { padding: 50px 40px; }
    }

    @media (max-width: 576px) {
      .container { padding: 0 25px; }
      .hero-title { font-size: 2.5rem; }
      .hero-subtitle { font-size: 1.2rem; }
      .hero-stats { grid-template-columns: 1fr; gap: 25px; padding: 40px 30px; }
      .stat-number { font-size: 3rem; }
      .section-title { font-size: 2.2rem; }
      .section-subtitle { font-size: 1.2rem; }
      .cta-title { font-size: 2.5rem; }
      .cta-subtitle { font-size: 1.3rem; }
      .footer-content { grid-template-columns: 1fr; }
      .mobile-menu { width: 100%; padding: 140px 30px 30px; }
      .btn { padding: 16px 32px; font-size: 1.1rem; }
      .contact-form { padding: 40px 30px; }
      .feature-card, .academy-card, .plan-card, .testimonial-card { padding: 40px 30px; }
    }
  `;

// ============== COMPONENT ==============
export default function Landing() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  const [academies, setAcademies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [contactLinks, setContactLinks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchLandingData();
        setAcademies(data.academies || []);
        setPlans(data.plans || []);
        setFaqs(data.faqs || []);
        setContactLinks(data.contact_links || []);
        setTestimonials(data.testimonials || []);
      } catch (error) {
        console.error("Error loading landing data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Features data
  const features = [
    {
      id: 1,
      title: "إدارة متكاملة",
      description: "نظام متكامل لإدارة الأكاديميات، المدرّبين، الطلاب، والخطط الدراسية",
      icon: "🎯"
    },
    {
      id: 2,
      title: "تقارير ذكية",
      description: "تقارير تفصيلية وإحصائيات حية لمتابعة أداء الأكاديمية",
      icon: "📊"
    },
    {
      id: 3,
      title: "سهولة الاستخدام",
      description: "واجهة مستخدم بديهية وسهلة التعلم لجميع المستخدمين",
      icon: "🚀"
    },
    {
      id: 4,
      title: "دعم فني",
      description: "فريق دعم فني متخصص على مدار الساعة لمساعدتك",
      icon: "🛠️"
    }
  ];

  // Stats data
  const stats = [
    { id: 1, number: "50+", label: "أكاديمية", icon: "🎓" },
    { id: 2, number: "1000+", label: "متدرب", icon: "👥" },
    { id: 3, number: "200+", label: "مدرب معتمد", icon: "⭐" },
    { id: 4, number: "98%", label: "رضا العملاء", icon: "❤️" }
  ];

  // Contact methods
  const contactMethods = [
    { id: 1, title: "الهاتف", info: "+1234567890", icon: <Icons.Phone /> },
    { id: 2, title: "البريد الإلكتروني", info: "info@academy.com", icon: <Icons.Email /> },
    { id: 3, title: "العنوان", info: "شارع الأكاديميات، الرياض، المملكة العربية السعودية", icon: <Icons.Location /> }
  ];

  // Footer links
  const footerLinks = {
    quickLinks: [
      { id: 1, text: "الرئيسية", url: "#" },
      { id: 2, text: "الأكاديميات", url: "#academies" },
      { id: 3, text: "الخطط", url: "#plans" },
      { id: 4, text: "المدربين", url: "#" },
      { id: 5, text: "الدورات", url: "#" }
    ],
    support: [
      { id: 1, text: "المساعدة", url: "#" },
      { id: 2, text: "الأسئلة الشائعة", url: "#faq" },
      { id: 3, text: "اتصل بنا", url: "#contact" },
      { id: 4, text: "سياسة الخصوصية", url: "#" },
      { id: 5, text: "شروط الخدمة", url: "#" }
    ],
    resources: [
      { id: 1, text: "المدونة", url: "#" },
      { id: 2, text: "الدروس المجانية", url: "#" },
      { id: 3, text: "الأدوات التعليمية", url: "#" },
      { id: 4, text: "المستندات", url: "#" }
    ]
  };

  // Handle FAQ toggle
  const toggleFaq = (id) => {
    setActiveFaq(activeFaq === id ? null : id);
  };

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <style>{styles}</style>
        <div className="spinner"></div>
        <h2>جارٍ تحميل الصفحة...</h2>
      </div>
    );
  }

  return (
    <div className="academy-landing">
      <style>{styles}</style>

      {/* Header */}
      <header className={`header ${scrolled ? "scrolled" : ""}`}>
        <div className="container">
          <div className="header-content">
            {/* Logo */}
            <a href="#" className="logo">
              <div className="logo-icon">
                <Icons.Graduation />
              </div>
              <div className="logo-text">أكاديميات</div>
            </a>

            {/* Navigation */}
            <nav>
              <ul className="nav-links">
                <li><a href="#" className="nav-link">الرئيسية</a></li>
                <li><a href="#academies" className="nav-link">الأكاديميات</a></li>
                <li><a href="#features" className="nav-link">المميزات</a></li>
                <li><a href="#plans" className="nav-link">الخطط</a></li>
                <li><a href="#testimonials" className="nav-link">آراء العملاء</a></li>
                <li><a href="#contact" className="nav-link">اتصل بنا</a></li>
              </ul>
            </nav>

            {/* Header Actions */}
            <div className="header-actions">
              <button className="search-btn">
                <Icons.Search />
              </button>
              <button 
                className="mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <Icons.Close /> : <Icons.Menu />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
          <ul className="mobile-nav-links">
            <li><a href="#" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <Icons.Home /> الرئيسية
            </a></li>
            <li><a href="#academies" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <Icons.Graduation /> الأكاديميات
            </a></li>
            <li><a href="#features" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <Icons.Star /> المميزات
            </a></li>
            <li><a href="#plans" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <Icons.Book /> الخطط
            </a></li>
            <li><a href="#testimonials" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <Icons.Quote /> آراء العملاء
            </a></li>
            <li><a href="#contact" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <Icons.Phone /> اتصل بنا
            </a></li>
          </ul>
          
          <div className="mobile-menu-actions">
            <button className="btn btn-primary" style={{ width: "100%" }}>
              <span>ابدأ الآن</span>
              <Icons.ArrowRight />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" id="home">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              منصة إدارة الأكاديميات
              <br />
              <span style={{ fontSize: "3.5rem" }}>نظام متكامل لإدارة التعليم</span>
            </h1>
            
            <p className="hero-subtitle">
              نظام متكامل لإدارة الأكاديميات، المدرّبين، الطلاب، الخطط، والاشتراكات.
              نوفر لك كل الأدوات التي تحتاجها لإدارة أكاديميتك بكل كفاءة واحترافية.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn btn-primary"
                onClick={() => navigate("/register")}
              >
                <span>ابدأ مجاناً</span>
                <Icons.ArrowRight />
              </button>
              <button className="btn btn-outline">
                <span>شاهد العرض التوضيحي</span>
                <Icons.Play />
              </button>
            </div>

            <div className="hero-stats">
              {stats.map((stat) => (
                <div key={stat.id} className="stat-item">
                  <div className="stat-icon">{stat.icon}</div>
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">مميزاتنا</div>
            <h2 className="section-title">لماذا تختار منصتنا؟</h2>
            <p className="section-subtitle">
              نوفر لك مجموعة متكاملة من المميزات التي تجعل إدارة أكاديميتك أسهل وأكثر كفاءة
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature) => (
              <div key={feature.id} className="feature-card">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academies Section */}
      <section className="academies" id="academies">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">الأكاديميات</div>
            <h2 className="section-title">الأكاديميات المتاحة</h2>
            <p className="section-subtitle">
              اكتشف مجموعة متنوعة من الأكاديميات المتخصصة في مختلف المجالات التعليمية
            </p>
          </div>

          {academies.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <p style={{ fontSize: "1.2rem", color: "var(--gray)" }}>
                لا يوجد أكاديميات متاحة حالياً
              </p>
            </div>
          ) : (
            <div className="academies-grid">
              {academies.map((academy) => (
                <div 
                  key={academy.id} 
                  className="academy-card"
                  onClick={() => navigate(`/academy/${academy.id}`)}
                >
                  <div className="academy-image">
                    {academy.image ? (
                      <img src={academy.image} alt={academy.name} />
                    ) : (
                      <div style={{
                        width: "100%",
                        height: "100%",
                        background: "var(--gradient-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "3rem"
                      }}>
                        <Icons.Graduation />
                      </div>
                    )}
                  </div>
                  
                  <div className="academy-content">
                    <h3 className="academy-title">{academy.name}</h3>
                    <p className="academy-description">
                      {academy.description || "أكاديمية متخصصة في تقديم التعليم المتميز"}
                    </p>
                    
                    <div className="academy-link">
                      <span>تصفح الأكاديمية</span>
                      <Icons.ArrowLeft />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Plans Section */}
      <section className="plans-section" id="plans">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">خطط الاشتراك</div>
            <h2 className="section-title">اختر الخطة المناسبة لك</h2>
            <p className="section-subtitle">
              خطط مرنة تناسب جميع احتياجاتك مع ميزات متقدمة ودعم فني متميز
            </p>
          </div>

          {plans.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <p style={{ fontSize: "1.2rem", color: "var(--gray)" }}>
                لا توجد خطط اشتراك متاحة حالياً
              </p>
            </div>
          ) : (
            <div className="plans-grid">
              {plans.map((plan, index) => (
                <div 
                  key={plan.id} 
                  className={`plan-card ${index === 1 ? "featured" : ""}`}
                >
                  {index === 1 && (
                    <div className="featured-badge">
                      <Icons.Star />
                      <span>الأكثر شهرة</span>
                    </div>
                  )}
                  
                  <div className="plan-header">
                    <h3 className="plan-title">{plan.title}</h3>
                    <div className="plan-price">
                      <div className="price-amount">{plan.price}$</div>
                      <div className="price-period">/ شهرياً</div>
                    </div>
                  </div>

                  <div className="plan-features">
                    <ul className="features-list">
                      {plan.features?.map((feature) => (
                        <li key={feature.id}>
                          <Icons.Check />
                          <span>{feature.feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button 
                    className={`btn ${index === 1 ? "btn-secondary" : "btn-outline"}`}
                    onClick={() => navigate(`/register?plan=${plan.id}`)}
                  >
                    <span>اشترك الآن</span>
                    <Icons.ArrowRight />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials" id="testimonials">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">آراء العملاء</div>
            <h2 className="section-title">ماذا يقول عملاؤنا؟</h2>
            <p className="section-subtitle">
              آراء وتقييمات من عملائنا الذين استفادوا من خدماتنا
            </p>
          </div>

          {testimonials.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <p style={{ fontSize: "1.2rem", color: "var(--gray)" }}>
                لا توجد آراء متاحة حالياً
              </p>
            </div>
          ) : (
            <div className="testimonials-slider">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="testimonial-card">
                  <p className="testimonial-content">"{testimonial.content}"</p>
                  
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {testimonial.name.charAt(0).toUpperCase()}
                    </div>
                    
                    <div className="author-info">
                      <h4 className="author-name">{testimonial.name}</h4>
                      <p className="author-role">عميل</p>
                      
                      <div className="rating">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className="star-icon">
                            <Icons.Star />
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq" id="faq">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">الأسئلة الشائعة</div>
            <h2 className="section-title">أسئلة متكررة</h2>
            <p className="section-subtitle">
              أجوبة على الأسئلة الأكثر شيوعاً حول منصتنا وخدماتنا
            </p>
          </div>

          {faqs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <p style={{ fontSize: "1.2rem", color: "var(--gray)" }}>
                لا توجد أسئلة شائعة متاحة حالياً
              </p>
            </div>
          ) : (
            <div className="faq-list">
              {faqs.map((faq) => (
                <div 
                  key={faq.id} 
                  className={`faq-item ${activeFaq === faq.id ? "active" : ""}`}
                >
                  <div 
                    className="faq-question"
                    onClick={() => toggleFaq(faq.id)}
                  >
                    <h3 className="question-text">{faq.question}</h3>
                    <div className="arrow-icon">
                      <Icons.ChevronRight />
                    </div>
                  </div>
                  
                  <div className="faq-answer">
                    <div className="faq-answer-content">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">جاهز لبدء رحلتك التعليمية؟</h2>
            <p className="cta-subtitle">
              انضم إلى آلاف الأكاديميات الناجحة واستفد من نظامنا المتكامل لإدارة التعليم
            </p>
            
            <div className="cta-buttons">
              <button 
                className="btn btn-primary"
                onClick={() => navigate("/register")}
              >
                <span>سجل مجاناً</span>
                <Icons.ArrowRight />
              </button>
              <button className="btn btn-glass">
                <span>اتصل بالمبيعات</span>
                <Icons.Phone />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact" id="contact">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">اتصل بنا</div>
            <h2 className="section-title">تواصل معنا</h2>
            <p className="section-subtitle">
              نحن هنا لمساعدتك في أي استفسار أو دعم تحتاجه
            </p>
          </div>

          <div className="contact-wrapper">
            {/* Contact Info */}
            <div className="contact-info">
              {contactMethods.map((method) => (
                <div key={method.id} className="contact-item">
                  <div className="contact-icon">
                    {method.icon}
                  </div>
                  <div>
                    <h4>{method.title}</h4>
                    <p>{method.info}</p>
                  </div>
                </div>
              ))}

              {/* Social Links */}
              {contactLinks.length > 0 && (
                <div className="social-links">
                  {contactLinks.map((link) => (
                    <a 
                      key={link.id}
                      href={link.link}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-social"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Contact Form */}
            <div className="contact-form">
              <h3>أرسل رسالة</h3>
              
              <form>
                <div className="form-group">
                  <label className="form-label">الاسم الكامل</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">البريد الإلكتروني</label>
                  <input 
                    type="email" 
                    className="form-input" 
                    placeholder="example@email.com"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">الرسالة</label>
                  <textarea 
                    className="form-input" 
                    placeholder="اكتب رسالتك هنا..."
                    rows="5"
                    style={{ minHeight: "150px" }}
                  ></textarea>
                </div>
                
                <button type="submit" className="btn btn-primary" style={{ width: "100%" }}>
                  <span>إرسال الرسالة</span>
                  <Icons.ArrowRight />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            {/* Column 1: About */}
            <div className="footer-column">
              <h3>منصة أكاديميات</h3>
              <p style={{ color: "rgba(255,255,255,0.8)", lineHeight: "1.8", marginBottom: "20px" }}>
                نظام متكامل لإدارة الأكاديميات التعليمية بأحدث التقنيات وأفضل الخدمات.
              </p>
            </div>

            {/* Column 2: Quick Links */}
            <div className="footer-column">
              <h3>روابط سريعة</h3>
              <ul className="footer-links">
                {footerLinks.quickLinks.map((link) => (
                  <li key={link.id}>
                    <a href={link.url}>
                      <Icons.ChevronRight />
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: Support */}
            <div className="footer-column">
              <h3>الدعم</h3>
              <ul className="footer-links">
                {footerLinks.support.map((link) => (
                  <li key={link.id}>
                    <a href={link.url}>
                      <Icons.ChevronRight />
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4: Newsletter */}
            <div className="footer-column">
              <h3>النشرة البريدية</h3>
              <p style={{ color: "rgba(255,255,255,0.8)", lineHeight: "1.8", marginBottom: "20px" }}>
                اشترك في نشرتنا البريدية لتصلك آخر التحديثات والعروض.
              </p>
              
              <div className="newsletter">
                <input 
                  type="email" 
                  className="newsletter-input" 
                  placeholder="بريدك الإلكتروني"
                />
                <button className="btn btn-primary" style={{ width: "100%" }}>
                  <span>اشترك</span>
                </button>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="copyright">
            <p>© {new Date().getFullYear()} منصة أكاديميات. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}